/** Exponential backoff with jitter for reconnect attempts. */

export interface BackoffOptions {
  /** Upper end of the first delay (ms). */
  baseMs: number;
  /** Upper bound for any single delay (ms). */
  maxMs: number;
  /** Growth of the delay per failed attempt. */
  factor: number;
  /**
   * Randomized fraction of the delay: 0 = fixed, 0.5 = anywhere in [delay/2, delay].
   * Jitter spreads out the reconnects of all players after a server restart.
   */
  jitter: number;
}

export const DEFAULT_BACKOFF: BackoffOptions = {
  baseMs: 500,
  maxMs: 10_000,
  factor: 2,
  jitter: 0.5,
};

/**
 * Delay before retry number `attempt` (0 = first retry). `random` returns floats in [0, 1).
 * With the defaults: ~0.25–0.5 s, 0.5–1 s, 1–2 s, … capped at 5–10 s.
 */
export function backoffDelay(
  attempt: number,
  random: () => number = Math.random,
  options: BackoffOptions = DEFAULT_BACKOFF,
): number {
  const ceiling = Math.min(options.maxMs, options.baseMs * options.factor ** Math.max(0, attempt));
  return Math.round(ceiling * (1 - options.jitter * random()));
}
